/**
 * DOM safety guard for third-party DOM mutation.
 *
 * Browser page translators (Chrome's built-in Google Translate, Gemini/AI reader extensions)
 * rewrite the text nodes React rendered: each original text node is replaced by a <font> wrapper
 * holding the translated copy. React keeps pointing at the node it created, so the next update
 * that removes or reorders that text — a socket status change, a metrics refresh, a tab switch —
 * calls removeChild/insertBefore with a node whose parent is no longer the one React expects, and
 * the DOM throws:
 *
 *   NotFoundError: Failed to execute 'removeChild' on 'Node':
 *   The node to be removed is not a child of this node.
 *
 * React cannot recover from a commit-phase failure, so the whole route unmounts to an error
 * screen. `translate="no"` in index.html stops the well-behaved translators; this guard covers
 * the extensions that ignore it, turning a fatal crash into a no-op on a node that is already
 * gone from the tree the caller believes it is in.
 *
 * The guard never changes behaviour for a well-formed call: it only intervenes when the DOM would
 * have thrown anyway.
 */

let installed = false;

export function installDomMutationGuard(): void {
  if (installed || typeof Node === "undefined") {
    return;
  }
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChildSafely<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      // Already detached (or re-parented by a translator). The caller's intent — "this node must
      // not be in the document" — is satisfied, so report success instead of throwing.
      warnOnce("removeChild", child);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBeforeSafely<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // The anchor React wanted to insert in front of was moved away. Appending keeps the node in
      // the tree — the ordering may differ from React's model, but the alternative is a crash.
      warnOnce("insertBefore", referenceNode);
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

const warned = new Set<string>();

function warnOnce(operation: string, node: Node): void {
  if (!import.meta.env.DEV || warned.has(operation)) {
    return;
  }
  warned.add(operation);
  console.warn(
    `[domSafety] Suppressed a failing ${operation}() call — the node is not a child of the ` +
      `target. This normally means a page translator or another extension rewrote the DOM. ` +
      `If no extension is active, it points at a real reconciliation bug worth investigating.`,
    node,
  );
}
